// The page's side of the media service worker's message protocol (DESIGN §6.2). Every call is a
// safe no-op without a service worker container or controller.
import { MEDIA_FAILURE_REASONS } from "./failureReason.js";

const DEFAULT_REPLY_TIMEOUT_MS = 2000;

export function createWorkerClient({ container, telemetry, guestSession, replyTimeoutMs = DEFAULT_REPLY_TIMEOUT_MS } = {}) {
  let lastBinding = null;
  let disposed = false;

  const post = (message) => container?.controller?.postMessage(message);

  /** Posts `message` with a reply port; resolves with the first reply, or null on no controller/timeout. */
  function ask(message) {
    const controller = container?.controller;
    if (!controller || disposed) return Promise.resolve(null);
    return new Promise((resolve) => {
      const channel = new MessageChannel();
      const finish = (value) => {
        clearTimeout(timer);
        channel.port1.onmessage = null;
        channel.port1.close();
        resolve(value);
      };
      const timer = setTimeout(() => finish(null), replyTimeoutMs);
      channel.port1.onmessage = (event) => finish(event.data ?? null);
      try {
        controller.postMessage(message, [channel.port2]);
      } catch {
        finish(null);
      }
    });
  }

  async function bind(binding) {
    if (!container) return false;
    lastBinding = binding;
    const reply = await ask({ type: "bind", binding });
    return reply?.type === "bound";
  }

  const onControllerChange = () => {
    if (lastBinding) bind(lastBinding);
  };

  // Only the reason enum leaves this function: the worker's message is never forwarded as is.
  const onMessage = (event) => {
    const type = event?.data?.type;
    if (type === "sw-error") {
      telemetry?.serviceWorkerError?.({ class: event.data.class, fingerprint: event.data.fingerprint });
      return;
    }
    if (type !== "session-lost" && type !== "session-changed") return;
    if (!guestSession?.binding) return; // already reset; the next binding re-arms this
    if (type === "session-lost") {
      const reason = typeof event.data.reason === "string" ? event.data.reason : "other";
      telemetry?.sessionEnded?.(reason, "media");
      guestSession.reset("session-lost");
    } else {
      guestSession.reset("session-changed");
    }
  };

  container?.addEventListener?.("controllerchange", onControllerChange);
  container?.addEventListener?.("message", onMessage);

  return {
    bind,
    purge(binding) {
      post({ type: "purge", binding });
    },
    purgeAll() {
      lastBinding = null;
      post({ type: "purge-all" });
    },
    prefetch(binding, items) {
      post({ type: "prefetch", binding, items });
    },
    keepalive() {
      post({ type: "keepalive" });
    },
    stats() {
      return ask({ type: "stats" });
    },
    /** The worker's reason for the last failure of `src`: a reason, null (none), or sw_timeout. */
    async failureReason(src) {
      const reply = await ask({ type: "failure", path: src });
      if (reply?.type !== "failure") return "sw_timeout";
      if (reply.reason === null || reply.reason === undefined) return null;
      return MEDIA_FAILURE_REASONS.includes(reply.reason) ? reply.reason : "other";
    },
    dispose() {
      disposed = true;
      container?.removeEventListener?.("controllerchange", onControllerChange);
      container?.removeEventListener?.("message", onMessage);
    },
  };
}
